import { deleteAPI, getAPI, patchAPI, postAPI } from "../../../api/Networking";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import {
  CreateOptionBody,
  CreateOptionValuesBody,
  DisplayType,
  OptionApi,
  OptionDetailApi,
  OptionListQuery,
  OptionValueApi,
  OptionValueListQuery,
  UpdateOptionBody,
  UpdateOptionValueBody,
  isDisplayType,
  normalizeOptionSortBy,
} from "../../../models/CatalogOptions/types";

const BASE = "/v1/catalog/options";

function asBool(v: unknown): boolean {
  if (v === true || v === 1 || v === "1" || v === "true") return true;
  return false;
}

function pickStr(v: unknown): string | null {
  if (v == null) return null;
  const s = typeof v === "string" ? v.trim() : String(v).trim();
  return s.length ? s : null;
}

function pickMeta(v: unknown): Record<string, unknown> | null {
  if (v == null) return null;
  if (typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  if (typeof v === "string") {
    try {
      const parsed = JSON.parse(v);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  }
  return null;
}

function normalizeDisplayType(raw: unknown): DisplayType | string {
  const s = pickStr(raw) ?? "dropdown";
  return isDisplayType(s) ? s : s;
}

function normalizeOption(raw: unknown): OptionApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    name: pickStr(r.name) ?? "",
    slug: pickStr(r.slug),
    display_type: normalizeDisplayType(r.display_type),
    position: Number(r.position ?? r.priority) || 0,
    is_active: asBool(r.is_active ?? r.active),
    created_by: r.created_by == null ? null : Number(r.created_by),
    updated_by: r.updated_by == null ? null : Number(r.updated_by),
    created_date: pickStr(r.created_date ?? r.created_at),
    modified_date: pickStr(r.modified_date ?? r.updated_at),
  };
}

function normalizeOptionValue(raw: unknown): OptionValueApi {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    id: Number(r.id) || 0,
    option_id: Number(r.option_id) || 0,
    value: pickStr(r.value) ?? "",
    sku_code: pickStr(r.sku_code),
    position: Number(r.position ?? r.priority) || 0,
    meta: pickMeta(r.meta),
    is_active: asBool(r.is_active ?? r.active),
    created_by: r.created_by == null ? null : Number(r.created_by),
    updated_by: r.updated_by == null ? null : Number(r.updated_by),
    created_date: pickStr(r.created_date ?? r.created_at),
    modified_date: pickStr(r.modified_date ?? r.updated_at),
  };
}

function normalizeOptionDetail(raw: unknown): OptionDetailApi {
  const base = normalizeOption(raw);
  const r = (raw ?? {}) as Record<string, unknown>;
  const valuesRaw = r.values;
  return {
    ...base,
    values: Array.isArray(valuesRaw)
      ? valuesRaw.map(normalizeOptionValue)
      : [],
  };
}

function unwrapResult(raw: unknown): unknown {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    if (
      !("list" in o) &&
      !("items" in o) &&
      o.result &&
      typeof o.result === "object"
    ) {
      return o.result;
    }
  }
  return raw;
}

function assertSuccess(response: {
  success?: boolean;
  message?: string;
}): void {
  if (response && response.success === false) {
    throw {
      response: { data: { message: response.message || "Request failed." } },
      message: response.message || "Request failed.",
    };
  }
}

export default class CatalogOptionsAPI {
  private normalizeListResponse(
    params: OptionListQuery,
    raw: unknown,
  ): PaginatedAPIResponse<OptionApi | OptionDetailApi> {
    if (Array.isArray(raw)) {
      const list = raw.map((row) => {
        const r = row as Record<string, unknown>;
        return Array.isArray(r.values)
          ? normalizeOptionDetail(row)
          : normalizeOption(row);
      });
      return {
        list,
        limit: params.limit ?? list.length,
        page_no: params.page_no ?? 1,
        total_count: list.length,
        total_pages: 1,
      };
    }

    const r = (raw ?? {}) as Record<string, unknown>;
    const rows = r.list ?? r.items ?? r.data;
    const list = Array.isArray(rows)
      ? rows.map((row) => {
          const item = row as Record<string, unknown>;
          return Array.isArray(item.values)
            ? normalizeOptionDetail(row)
            : normalizeOption(row);
        })
      : [];

    return {
      limit: Number(r.limit ?? params.limit ?? 20) || 20,
      page_no: Number(r.page_no ?? params.page_no ?? 1) || 1,
      total_count: Number(r.total_count ?? list.length) || 0,
      total_pages: Number(r.total_pages ?? 1) || 1,
      list,
    };
  }

  listOptions = async (
    params: OptionListQuery,
  ): Promise<PaginatedAPIResponse<OptionApi | OptionDetailApi>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no != null)
        queryParams.append("page_no", String(params.page_no));
      if (params.limit != null)
        queryParams.append("limit", String(params.limit));
      queryParams.append("sort_by", normalizeOptionSortBy(params.sort_by));
      if (params.sort_type != null)
        queryParams.append("sort_type", String(params.sort_type));
      const term = params.search?.trim();
      if (term) queryParams.append("search", term);
      if (params.active !== undefined)
        queryParams.append("active", String(params.active));
      if (params.id != null) queryParams.append("id", String(params.id));
      if (params.category_id != null)
        queryParams.append("category_id", String(params.category_id));
      if (params.include_values !== undefined) {
        const includeValues = params.include_values;
        const flag =
          includeValues === true ||
          includeValues === 1 ||
          includeValues === "true"
            ? "true"
            : "false";
        queryParams.append("include_values", flag);
      }

      const qs = queryParams.toString();
      const response = await getAPI(`${BASE}${qs ? `?${qs}` : ""}`);
      assertSuccess(response);
      return this.normalizeListResponse(
        params,
        unwrapResult(response?.result),
      );
    } catch (error) {
      throw error;
    }
  };

  getOptionById = async (id: number): Promise<OptionDetailApi> => {
    try {
      const response = await getAPI(`${BASE}/${id}`);
      assertSuccess(response);
      return normalizeOptionDetail(response.result);
    } catch (error) {
      throw error;
    }
  };

  listOptionValues = async (
    optionId: number,
    params: OptionValueListQuery = {},
  ): Promise<PaginatedAPIResponse<OptionValueApi>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page_no != null)
        queryParams.append("page_no", String(params.page_no));
      if (params.limit != null)
        queryParams.append("limit", String(params.limit));
      const term = params.search?.trim();
      if (term) queryParams.append("search", term);
      if (params.active !== undefined)
        queryParams.append("active", String(params.active));

      const qs = queryParams.toString();
      const response = await getAPI(
        `${BASE}/${optionId}/values${qs ? `?${qs}` : ""}`,
      );
      assertSuccess(response);
      return this.normalizeValuesListResponse(
        params,
        unwrapResult(response?.result),
      );
    } catch (error) {
      throw error;
    }
  };

  private normalizeValuesListResponse(
    params: OptionValueListQuery,
    raw: unknown,
  ): PaginatedAPIResponse<OptionValueApi> {
    if (Array.isArray(raw)) {
      const list = raw.map(normalizeOptionValue);
      return {
        list,
        limit: params.limit ?? list.length,
        page_no: params.page_no ?? 1,
        total_count: list.length,
        total_pages: 1,
      };
    }

    const r = (raw ?? {}) as Record<string, unknown>;
    const rows = r.list ?? r.items ?? r.data ?? r.values;
    const list = Array.isArray(rows) ? rows.map(normalizeOptionValue) : [];

    return {
      limit: Number(r.limit ?? params.limit ?? 20) || 20,
      page_no: Number(r.page_no ?? params.page_no ?? 1) || 1,
      total_count: Number(r.total_count ?? list.length) || 0,
      total_pages: Number(r.total_pages ?? 1) || 1,
      list,
    };
  }

  createOption = async (body: CreateOptionBody): Promise<OptionDetailApi> => {
    try {
      const response = await postAPI(BASE, body);
      assertSuccess(response);
      return normalizeOptionDetail(response.result);
    } catch (error) {
      throw error;
    }
  };

  updateOption = async (
    id: number,
    body: UpdateOptionBody,
  ): Promise<OptionDetailApi> => {
    try {
      const response = await patchAPI(`${BASE}/${id}`, body);
      assertSuccess(response);
      return normalizeOptionDetail(response.result);
    } catch (error) {
      throw error;
    }
  };

  deleteOption = async (id: number): Promise<OptionDetailApi> => {
    try {
      const response = await deleteAPI(`${BASE}/${id}`);
      assertSuccess(response);
      return normalizeOptionDetail(response.result);
    } catch (error) {
      throw error;
    }
  };

  createValues = async (
    optionId: number,
    body: CreateOptionValuesBody,
  ): Promise<OptionValueApi[]> => {
    try {
      const response = await postAPI(`${BASE}/${optionId}/values`, body);
      assertSuccess(response);
      const raw = response.result;
      if (Array.isArray(raw)) return raw.map(normalizeOptionValue);
      if (raw && typeof raw === "object") {
        return [normalizeOptionValue(raw)];
      }
      return [];
    } catch (error) {
      throw error;
    }
  };

  updateValue = async (
    optionId: number,
    valueId: number,
    body: UpdateOptionValueBody,
  ): Promise<OptionValueApi> => {
    try {
      const response = await patchAPI(
        `${BASE}/${optionId}/values/${valueId}`,
        body,
      );
      assertSuccess(response);
      return normalizeOptionValue(response.result);
    } catch (error) {
      throw error;
    }
  };

  deleteValue = async (
    optionId: number,
    valueId: number,
  ): Promise<OptionValueApi> => {
    try {
      const response = await deleteAPI(
        `${BASE}/${optionId}/values/${valueId}`,
      );
      assertSuccess(response);
      return normalizeOptionValue(response.result);
    } catch (error) {
      throw error;
    }
  };
}
